export const services = [
    {
        id: 'ai-compliance',
        title: 'AI Compliance',
        slug: 'ai-compliance',
        category: 'SB 574 Readiness for Law Firms',
        badge: 'New',
        description: 'SB 574 makes AI use a statutory duty for California attorneys on January 1, 2027. We are the qualified IT and cybersecurity professional the State Bar says a lawyer may need to consult: we audit every AI tool your firm uses, build the controls that are missing, and keep the evidence file current.',
        icon: '§',
        overview: {
            title: 'Overview',
            content: 'SB 574 (Chapter 858, Statutes of 2026) adds five duties to the Business and Professions Code and the Code of Civil Procedure, and the State Bar\'s 2026 COPRAC Practical Guidance sets expectations for policy, training and supervision on top of them. "We\'re careful" is not a record. SB 574 Readiness is a documented, defensible process that turns your firm\'s AI use into evidence a court, carrier or client will accept. It runs on one coded control library and one persistent evidence file per firm, and it was built and run for real inside a working Los Angeles law firm. It is not software and it is not legal advice.',
        },
        benefits: {
            title: 'What the Firm Has to Be Able to Show',
            items: [
                {
                    title: 'No delegation of the practice of law (B&P §6068.1)',
                    description: 'Attorney judgment at the substantive points of every AI-assisted workflow. We deliver a workflow map with named human review checkpoints, especially for agentic tools.',
                },
                {
                    title: 'Confidential information stays protected (B&P §6068.1(a)(3)(A))',
                    description: 'Which tools are allowed, what each may receive, and why its data handling qualifies. We test each tool\'s access controls, retention and training terms, and certify which inputs it may receive.',
                },
                {
                    title: 'AI output is verified before use (B&P §6068.1)',
                    description: 'A defined verification step people actually follow, delivered as a protocol and checklist by work type.',
                },
                {
                    title: 'Every court citation is personally verified (CCP §128.7(b)(2))',
                    description: 'Only the signing attorney can verify, so we design the pre-filing workflow and keep the log that proves it was done. Sanctions under this rule land on the lawyers and the firm, not the client.',
                },
            ],
        },
        features: {
            title: 'What We Deliver',
            items: [
                'AI tool register, including personal accounts',
                'Vendor data-handling assessment against a 29-requirement standard',
                'Permitted-inputs matrix built on the §6068.1(b)(2) definition of personal identifying information',
                'Firm AI use policy traced to the 2026 COPRAC guidance',
                'Output verification protocol and checklists by work type',
                'Pre-filing citation verification workflow and log',
                'Court disclosure decision rule and template language, flagged for attorney sign-off',
                'Agentic workflow review checkpoints',
                'Engagement-letter and fee-agreement language',
                'Staff training and signed acknowledgments',
            ],
        },
        technologies: {
            title: 'Built On',
            items: [
                'SB 574 (Chapter 858)',
                'B&P §6068.1',
                'CCP §128.7',
                'State Bar COPRAC 2026 Practical Guidance',
                'Rules of Professional Conduct 1.1, 1.6, 5.1 to 5.3',
                'Vendor AI System Standard (9 control groups)',
            ],
        },
        useCases: {
            title: 'When the Question Arrives',
            cases: [
                {
                    title: 'Client AI questionnaire',
                    description: 'An institutional client asks how you govern AI. Answer with a findings report and control scorecard, not a paragraph about being careful.',
                },
                {
                    title: 'Malpractice carrier renewal',
                    description: 'Carriers now ask about AI use. Hand them a current evidence file and a quarterly attestation.',
                },
                {
                    title: 'Adopting an agentic tool',
                    description: 'More autonomy means more oversight. Every new tool is reviewed before use, with access scoped to what it actually needs.',
                },
                {
                    title: 'Litigation-heavy practices',
                    description: 'Every citation in every filing, AI-sourced or not, needs a record that the signing attorney checked it.',
                },
            ],
        },
        process: {
            title: 'Audit, Consult, Maintain',
            steps: [
                {
                    title: 'Audit: Readiness Audit, $4,500 fixed, about 3 weeks',
                    description: 'Inventory every AI touchpoint, gap analysis against the five SB 574 duties and the COPRAC duty areas, vendor review of each tool including the §6068.1 access-restriction test, and a fix list ordered by exposure.',
                },
                {
                    title: 'Consult: Implementation, fixed fee scoped from the Audit',
                    description: 'Two to six weeks building the controls the Audit found missing: policy, permitted-inputs matrix, citation workflow and log, disclosure protocol, agentic checkpoints, client and fee language, and training.',
                },
                {
                    title: 'Maintain: Compliance Retainer, $1,500 to $2,500 per month',
                    description: 'Monthly new-tool reviews, quarterly vendor reassessment and log sampling, annual policy and training refresh, and on-demand help with carrier and client questionnaires.',
                },
            ],
        },
        pricing: {
            title: 'Pricing',
            description: 'Readiness Audit: $4,500 fixed. Implementation: fixed fee scoped from the Audit. Compliance Retainer: $1,500 to $2,500 per month by attorney count.',
            note: 'Summary for discussion, not legal advice. BuzBiz is not a law firm; your attorneys keep every legal judgment.',
        },
    },
    {
        id: 'aio',
        title: 'Artificial Intelligence Optimization',
        slug: 'aio',
        category: 'AI & Search',
        description: 'Get your website and brand featured in ChatGPT, Google AI Overviews, Perplexity, and other AI engines. We optimize your content and technical setup so AI systems cite you as a trusted source.',
        icon: '🤖',
        overview: {
            title: 'Overview',
            content: 'AIO (Artificial Intelligence Optimization) ensures your site is structured and written so that AI assistants and generative search engines discover, understand, and cite you. We combine technical optimization, content calibration, and ongoing monitoring to grow your visibility in AI-driven search and answers.',
        },
        benefits: {
            title: 'Key Benefits',
            items: [
                {
                    title: 'Visibility in AI Answers',
                    description: 'Increase citations in ChatGPT, Google AI Overviews, Perplexity, and similar tools so users see your brand when they ask relevant questions.',
                },
                {
                    title: 'Structured for Machines',
                    description: 'Schema, content clarity, and site structure tuned so AI systems can reliably extract and attribute your expertise.',
                },
                {
                    title: 'Ongoing Monitoring',
                    description: 'Track how AI engines represent your brand and content, with reports and recommendations to improve over time.',
                },
                {
                    title: 'Competitive Edge',
                    description: 'Early adopters of AIO typically see 5–15x ROI within 6 months as AI referral traffic and brand authority grow.',
                },
            ],
        },
        features: {
            title: 'What We Do',
            items: [
                'AI search and citation monitoring',
                'Content optimization for LLM clarity and attribution',
                'Schema and technical site optimization',
                'AI Overviews and answer calibration',
                'Competitor and query tracking',
                'Monthly reporting and strategy updates',
            ],
        },
        technologies: {
            title: 'Channels We Optimize For',
            items: [
                'ChatGPT & OpenAI Search',
                'Google AI Overviews',
                'Perplexity',
                'Bing Copilot',
                'Other LLM-powered search and answer engines',
            ],
        },
        useCases: {
            title: 'Who It’s For',
            cases: [
                {
                    title: 'Brands & Publishers',
                    description: 'Establish authority in your niche so AI systems recommend your content when users ask related questions.',
                },
                {
                    title: 'E‑commerce & Local',
                    description: 'Improve product and local visibility in AI-generated answers and shopping recommendations.',
                },
                {
                    title: 'B2B & SaaS',
                    description: 'Be cited as the go-to solution in your category when prospects ask AI tools for recommendations.',
                },
            ],
        },
        process: {
            title: 'Our Process',
            steps: [
                {
                    title: 'Audit & Baseline',
                    description: 'Assess how AI engines currently discover and cite your site, and identify high-impact queries and content gaps.',
                },
                {
                    title: 'Strategy & Roadmap',
                    description: 'Define content and technical changes, prioritization, and success metrics aligned with your business goals.',
                },
                {
                    title: 'Optimization',
                    description: 'Implement schema, content updates, and structural changes so AI systems can reliably use your content.',
                },
                {
                    title: 'Launch & Monitor',
                    description: 'Deploy optimizations and set up ongoing monitoring and reporting to track citations and referrals.',
                },
                {
                    title: 'Iterate',
                    description: 'Refine based on performance data and changes in how AI engines surface and attribute sources.',
                },
            ],
        },
        pricing: {
            title: 'Pricing',
            description: 'Transparent monthly plans from monitoring and recommendations through full done-for-you optimization. See our AIO landing page for detailed tiers and pricing.',
            note: 'Start at /aio for plan comparison and to request a quote.',
        },
    },
    {
        id: 'ai-implementation',
        title: 'AI Implementation',
        slug: 'ai-implementation',
        category: 'Secure & Compliant Automation',
        description: 'Put AI to work without putting your business at risk. We design and build secure, compliant workflow automations that connect your existing systems, keep sensitive data where it belongs, and leave a documented record of how every decision is made.',
        icon: '⚙️',
        overview: {
            title: 'Overview',
            content: 'Most AI rollouts start with a tool and worry about security and compliance later. We work the other way around. Every automation we build starts from the data it touches and the rules your business answers to, then adds AI where it earns its place. The result is workflow automation that saves real time, holds up to a security review, and comes with the documentation a client, carrier or regulator will ask for. Our AI implementations run on the same control framework behind our AI Compliance practice, so what we build is ready to audit from day one.',
        },
        benefits: {
            title: 'Key Benefits',
            items: [
                {
                    title: 'Secure by Default',
                    description: 'Least-privilege access, encryption in transit and at rest, and secrets kept out of prompts, logs and third-party tools.',
                },
                {
                    title: 'Compliant by Design',
                    description: 'Each workflow is mapped to the regulations and client obligations that apply to you, with data handling documented before anything goes live.',
                },
                {
                    title: 'Human Oversight Where It Matters',
                    description: 'Named review points at the substantive steps of every AI-assisted workflow, so automation speeds up the work without taking judgment out of it.',
                },
                {
                    title: 'Audit-Ready Records',
                    description: 'Logs and evidence of what each automation did, with which data, and who approved it, ready to show on request.',
                },
                {
                    title: 'Real Time Savings',
                    description: 'Automate repetitive intake, document, data and reporting work so your team can focus on higher-value tasks.',
                },
            ],
        },
        features: {
            title: 'What We Build',
            items: [
                'Secure AI-assisted workflow automation and orchestration',
                'Business system integrations with scoped, least-privilege access',
                'Document processing with sensitive-data controls',
                'Automated intake and qualification workflows',
                'Data pipelines with validation and error handling',
                'Agentic workflows with human review checkpoints',
                'AI vendor and model vetting before deployment',
                'Permitted-inputs rules for what data each tool may receive',
                'Audit logging and evidence capture',
                'Monitoring, alerting and periodic reassessment',
            ],
        },
        technologies: {
            title: 'Technologies We Use',
            items: [
                'Claude and leading LLM platforms',
                'Python, Node.js',
                'Workflow and orchestration engines',
                'API integration platforms',
                'Cloud infrastructure (AWS)',
                'Logging, monitoring and SIEM tooling',
            ],
        },
        useCases: {
            title: 'Common Use Cases',
            cases: [
                {
                    title: 'Client and Customer Intake',
                    description: 'Automate intake, verification and qualification while keeping personal information inside approved, access-restricted systems.',
                },
                {
                    title: 'Document Processing',
                    description: 'Extract, classify and route documents with AI, with sensitive fields protected and every step logged.',
                },
                {
                    title: 'Regulated Industries',
                    description: 'Law firms, financial services and other regulated businesses that need automation they can defend to a client, carrier or regulator.',
                },
                {
                    title: 'Reporting and Data Operations',
                    description: 'Compile and distribute reports from multiple systems automatically, with validation and a clear audit trail.',
                },
            ],
        },
        process: {
            title: 'Our Process',
            steps: [
                {
                    title: 'Discovery & Risk Mapping',
                    description: 'Map current workflows, the data each one touches and the rules that apply, then identify where automation and AI add the most value safely.',
                },
                {
                    title: 'Secure Solution Design',
                    description: 'Design workflows with access scoping, data handling rules and human review points built in, and vet every AI tool and vendor involved.',
                },
                {
                    title: 'Build & Security Testing',
                    description: 'Develop and test in a controlled environment, including security testing and verification of AI output before anything reaches production.',
                },
                {
                    title: 'Deployment & Training',
                    description: 'Deploy with documentation, logging and monitoring in place, and train your team on how to use, review and escalate.',
                },
                {
                    title: 'Monitor & Reassess',
                    description: 'Continuously monitor performance and reassess as models, vendors and regulations change, expanding automation as it proves itself.',
                },
            ],
        },
        pricing: {
            title: 'Pricing',
            description: 'Pricing based on scope, integrations and compliance requirements. We offer project-based builds and ongoing managed automation with monitoring and reassessment.',
            note: 'Schedule a consultation to discuss your automation needs and receive a detailed proposal.',
        },
    },
    {
        id: 'cyber-security',
        title: 'Cyber Security',
        slug: 'cyber-security',
        category: 'Security Services',
        description: 'Protect your digital assets with comprehensive cybersecurity solutions. From security assessments to ongoing monitoring, we help safeguard your business against evolving threats while ensuring compliance with industry standards.',
        icon: '🔒',
        overview: {
            title: 'Overview',
            content: 'Our cybersecurity services protect your business from threats, vulnerabilities, and compliance risks. We provide end-to-end security solutions from initial assessment through ongoing monitoring, ensuring your digital infrastructure remains secure and compliant.',
        },
        benefits: {
            title: 'Key Benefits',
            items: [
                {
                    title: 'Threat Protection',
                    description: 'Comprehensive security measures that protect against malware, phishing, data breaches, and other cyber threats.',
                },
                {
                    title: 'Compliance Assurance',
                    description: 'Ensure your systems meet industry standards and regulatory requirements (GDPR, HIPAA, PCI-DSS, etc.).',
                },
                {
                    title: 'Risk Mitigation',
                    description: 'Identify and address vulnerabilities before they can be exploited, reducing your risk exposure.',
                },
                {
                    title: 'Security Monitoring',
                    description: '24/7 monitoring and threat detection to identify and respond to security incidents quickly.',
                },
                {
                    title: 'Peace of Mind',
                    description: 'Know your business is protected with professional security services, allowing you to focus on growth.',
                },
            ],
        },
        features: {
            title: 'Core Features',
            items: [
                'Security audits and vulnerability assessments',
                'Penetration testing',
                'Secure development practices',
                'Security architecture review',
                'Compliance audits and remediation',
                'Security policy development',
                'Incident response planning',
                'Ongoing security monitoring',
                'Security training and awareness',
                'Threat intelligence and analysis',
            ],
        },
        technologies: {
            title: 'Security Tools & Standards',
            items: [
                'OWASP Security Standards',
                'Security Scanning Tools',
                'Penetration Testing Frameworks',
                'SIEM Solutions',
                'Compliance Management Tools',
                'Encryption Technologies',
            ],
        },
        useCases: {
            title: 'Common Use Cases',
            cases: [
                {
                    title: 'Pre-Launch Security',
                    description: 'Comprehensive security assessment and hardening before launching new applications or systems.',
                },
                {
                    title: 'Compliance Preparation',
                    description: 'Audit systems, identify gaps, and implement controls to meet regulatory requirements.',
                },
                {
                    title: 'Incident Response',
                    description: 'Rapid response to security incidents, including investigation, containment, and recovery.',
                },
                {
                    title: 'Ongoing Security',
                    description: 'Continuous monitoring, regular assessments, and proactive threat management to maintain security posture.',
                },
            ],
        },
        process: {
            title: 'Our Process',
            steps: [
                {
                    title: 'Security Assessment',
                    description: 'Evaluate your current security posture, identify vulnerabilities, and assess compliance with relevant standards.',
                },
                {
                    title: 'Remediation Planning',
                    description: 'Develop a prioritized plan to address security gaps and compliance issues based on risk and business impact.',
                },
                {
                    title: 'Implementation',
                    description: 'Implement security controls, policies, and monitoring systems to protect your infrastructure.',
                },
                {
                    title: 'Testing & Validation',
                    description: 'Conduct penetration testing and security validation to ensure controls are effective.',
                },
                {
                    title: 'Ongoing Management',
                    description: 'Provide continuous monitoring, regular assessments, and security updates to maintain protection.',
                },
            ],
        },
        pricing: {
            title: 'Pricing',
            description: 'Flexible pricing models including project-based assessments, retainer-based ongoing services, and managed security services.',
            note: 'Contact us to discuss your security needs and receive a customized security proposal.',
        },
    },
];
export const getServiceBySlug = (slug) => {
    return services.find((service) => service.slug === slug);
};
export const getAllServiceSlugs = () => {
    return services.map((service) => service.slug);
};
